import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search, Package, Store, Landmark, Loader2, LayoutGrid, BadgePercent, MapPin, Boxes,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { searchAPI } from '@/lib/api';
import { roleScreens } from './HomeTiles';

const MIN_QUERY = 2;
const DEBOUNCE_MS = 250;
const MAX_PER_GROUP = 5;
const MAX_SCREENS = 4;

// Network page that lists (and invites) each user type.
const NETWORK_PAGE_FOR = {
  distributor: '/distributors',
  manufacturer: '/suppliers',
  shopkeeper: '/shopkeepers',
  farmer: '/customers',
};

const CONNECTION_LABEL = {
  connected: 'homeSearch.connected',
  pending: 'homeSearch.pending',
};

function businessName(u) {
  return u.business_name || u.company_name || u.shop_name || u.full_name || '';
}

// Where a tapped result goes. Exported for the regression tests.
export function resultHref(item, viewer) {
  if (item.kind === 'screen') return item.path;
  if (item.kind === 'deal') return `/deals?deal=${encodeURIComponent(item.id)}`;
  if (item.kind === 'plot') return '/farm';
  if (item.kind === 'stock') return '/inventory';
  if (item.kind === 'product') {
    const ownProduct = viewer?.user_type === 'manufacturer' && item.manufacturer_id === viewer?.id;
    const base = ownProduct ? '/products' : '/marketplace';
    return `${base}?product=${encodeURIComponent(item.id)}`;
  }
  if (item.kind === 'user') {
    const page = NETWORK_PAGE_FOR[item.user_type] || '/dashboard';
    if (item.connection) return page;
    return `${page}?invite=${encodeURIComponent(businessName(item))}`;
  }
  return '/government';
}

function useSearchResults(query) {
  const [state, setState] = useState({ results: null, loading: false });
  const latest = useRef(0);

  useEffect(() => {
    const q = query.trim();
    if (q.length < MIN_QUERY) {
      latest.current += 1;
      setState({ results: null, loading: false });
      return undefined;
    }
    const ticket = ++latest.current;
    setState((s) => ({ ...s, loading: true }));
    const timer = setTimeout(async () => {
      try {
        const res = await searchAPI.search(q);
        if (ticket === latest.current) setState({ results: res.data, loading: false });
      } catch {
        // Typeahead is a convenience: on failure just hide it. Enter
        // still runs the role's full search.
        if (ticket === latest.current) setState({ results: null, loading: false });
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  return state;
}

// The app's own screens whose name matches, in the viewer's language
// (English names match too). One result per screen.
export function matchScreens(query, role, t) {
  const q = query.trim().toLowerCase();
  if (q.length < MIN_QUERY) return [];
  const seen = new Set();
  const out = [];
  for (const screen of roleScreens(role)) {
    const label = t(screen.labelKey);
    const english = t(screen.labelKey, { lng: 'en' });
    if (seen.has(screen.path)) continue;
    if (!label.toLowerCase().includes(q) && !english.toLowerCase().includes(q)) continue;
    seen.add(screen.path);
    out.push({ id: screen.key, kind: 'screen', title: label, path: screen.path });
  }
  return out.slice(0, MAX_SCREENS);
}

/**
 * Home-screen search bar with a grouped typeahead: the app's screens,
 * products, the viewer's own stock or plots, deals, businesses and
 * government updates. Choosing a result opens it; pressing Enter with
 * nothing highlighted calls `onSubmit`, the role's full search page.
 */
export default function HomeSearch({
  value,
  onChange,
  onSubmit,
  placeholderKey = 'homeSearch.placeholder',
  labelKey = 'homeSearch.label',
  testId = 'home-search-input',
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const listId = useId();
  const wrapperRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const { results, loading } = useSearchResults(value);

  const screens = useMemo(
    () => matchScreens(value, user?.user_type, t),
    [value, user?.user_type, t],
  );

  const groups = useMemo(() => {
    const take = (rows) => (rows || []).slice(0, MAX_PER_GROUP);
    const r = results || {};
    let offset = 0;
    return [
      { key: 'screens', icon: LayoutGrid, items: screens },
      { key: 'products', icon: Package, items: take(r.products).map((p) => ({ ...p, kind: 'product' })) },
      { key: 'stock', icon: Boxes, items: take(r.stock).map((x) => ({ ...x, kind: 'stock' })) },
      { key: 'plots', icon: MapPin, items: take(r.plots).map((x) => ({ ...x, kind: 'plot' })) },
      { key: 'deals', icon: BadgePercent, items: take(r.deals).map((d) => ({ ...d, kind: 'deal' })) },
      { key: 'businesses', icon: Store, items: take(r.users).map((u) => ({ ...u, kind: 'user' })) },
      { key: 'updates', icon: Landmark, items: take(r.government).map((g) => ({ ...g, kind: 'update' })) },
    ]
      .filter((g) => g.items.length > 0)
      .map((g) => {
        const group = { ...g, offset };
        offset += g.items.length;
        return group;
      });
  }, [results, screens]);

  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  const showPanel = open && value.trim().length >= MIN_QUERY && (loading || results || screens.length > 0);

  useEffect(() => { setActive(-1); }, [results, screens]);

  useEffect(() => {
    const onDocDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, []);

  const choose = (item) => {
    setOpen(false);
    navigate(resultHref(item, user));
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      // First Escape closes the panel and keeps the text; without
      // preventDefault a type="search" input clears itself. A second
      // Escape (panel already closed) gets the native clear.
      if (showPanel) {
        e.preventDefault();
        setOpen(false);
      }
      return;
    }
    if (!flat.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => (i + 1) % flat.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i <= 0 ? flat.length - 1 : i - 1));
    } else if (e.key === 'Enter' && open && active >= 0) {
      e.preventDefault();
      choose(flat[active]);
    }
  };

  const optionId = (i) => `${listId}-opt-${i}`;

  return (
    <div ref={wrapperRef} className="relative mb-5">
      <form
        onSubmit={(e) => { setOpen(false); onSubmit(e); }}
        role="search"
      >
        <label className="sr-only" htmlFor={testId}>
          {t(labelKey, { defaultValue: 'Search' })}
        </label>
        <div className="flex items-center gap-2 rounded-full bg-white border border-farm-forest/10 px-4 py-2.5 shadow-sm">
          <Search className="w-4 h-4 text-farm-forest/50 flex-shrink-0" />
          <input
            id={testId}
            type="search"
            value={value}
            onChange={(e) => { onChange(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            placeholder={t(placeholderKey, {
              defaultValue: 'Search for anything',
            })}
            role="combobox"
            aria-expanded={Boolean(showPanel)}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={active >= 0 ? optionId(active) : undefined}
            autoComplete="off"
            className="flex-1 bg-transparent text-sm text-farm-forest placeholder:text-farm-forest/50 focus:outline-none"
            data-testid={testId}
          />
          {loading && <Loader2 className="w-4 h-4 animate-spin text-farm-forest/40" aria-hidden />}
        </div>
      </form>

      {showPanel && (
        <div
          className="absolute left-0 right-0 top-full mt-2 z-40 bg-white rounded-2xl shadow-lg border border-farm-forest/10 max-h-[60vh] overflow-y-auto"
          data-testid="home-search-results"
        >
          <div id={listId} role="listbox" aria-label={t(labelKey, { defaultValue: 'Search' })}>
            {groups.map((group) => {
              const headingId = `${listId}-${group.key}`;
              return (
                <div key={group.key} role="group" aria-labelledby={headingId}>
                  <p id={headingId} className="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-farm-forest/50">
                    {t(`homeSearch.groups.${group.key}`)}
                  </p>
                  {group.items.map((item, n) => {
                    const i = group.offset + n;
                    return (
                      <div
                        key={`${item.kind}-${item.id}`}
                        id={optionId(i)}
                        role="option"
                        aria-selected={i === active}
                        tabIndex={-1}
                        // Keep focus in the input so blur doesn't close the
                        // panel before the click lands.
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => choose(item)}
                        // Arrow keys + Enter are handled on the input; this
                        // covers an assistive tech that focuses the option.
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            choose(item);
                          }
                        }}
                        onMouseEnter={() => setActive(i)}
                        className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer ${i === active ? 'bg-farm-cream' : ''}`}
                        data-testid={`home-search-${item.kind}`}
                      >
                        <group.icon className="w-4 h-4 text-farm-primary flex-shrink-0" aria-hidden />
                        <ResultText item={item} t={t} />
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
          {!loading && flat.length === 0 && (
            <p className="px-4 py-3 text-sm text-farm-forest/70" data-testid="home-search-empty">
              {t('homeSearch.empty', { q: value.trim() })}
            </p>
          )}
          <p className="px-4 py-2 border-t border-farm-forest/10 text-[11px] text-farm-forest/50">
            {t('homeSearch.enterHint')}
          </p>
        </div>
      )}
    </div>
  );
}

function Line({ title, detail }) {
  return (
    <div className="min-w-0">
      <p className="text-sm font-medium text-farm-forest truncate">{title}</p>
      {detail && <p className="text-[11px] text-farm-forest/60 truncate">{detail}</p>}
    </div>
  );
}

function ResultText({ item, t }) {
  if (item.kind === 'screen') return <Line title={item.title} />;
  if (item.kind === 'plot') return <Line title={item.plot_name} detail={item.crop_type} />;
  if (item.kind === 'stock') {
    return (
      <Line
        title={item.product_name}
        detail={t('homeSearch.inStock', { n: item.stock_quantity ?? 0 })}
      />
    );
  }
  if (item.kind === 'deal') return <Line title={item.title} detail={item.seller_name} />;
  if (item.kind === 'product') {
    return (
      <div className="min-w-0">
        <p className="text-sm font-medium text-farm-forest truncate">{item.name}</p>
        {item.manufacturer && (
          <p className="text-[11px] text-farm-forest/60 truncate">{item.manufacturer}</p>
        )}
      </div>
    );
  }
  if (item.kind === 'user') {
    const statusKey = CONNECTION_LABEL[item.connection];
    const status = statusKey ? t(statusKey) : null;
    const detail = [t(`common.role.${item.user_type}`, item.user_type), item.location, status]
      .filter(Boolean)
      .join(' · ');
    return (
      <div className="min-w-0">
        <p className="text-sm font-medium text-farm-forest truncate">{businessName(item)}</p>
        <p className="text-[11px] text-farm-forest/60 truncate">{detail}</p>
      </div>
    );
  }
  return (
    <div className="min-w-0">
      <p className="text-sm font-medium text-farm-forest truncate">{item.title}</p>
      {item.source && <p className="text-[11px] text-farm-forest/60 truncate">{item.source}</p>}
    </div>
  );
}
